import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import styles from './EmptyState.module.css';
import { Button, type ButtonProps } from '../Button';

export type EmptyStateSize = 'sm' | 'md' | 'lg';

export interface EmptyStateAction {
  /** Button label */
  label: string;
  /** Click handler */
  onClick: () => void;
  /** Button variant */
  variant?: ButtonProps['variant'];
}

export interface EmptyStateProps extends HTMLAttributes<HTMLDivElement> {
  /** Icon or illustration to display */
  icon?: ReactNode;
  /** Title text */
  title: string;
  /** Description text */
  description?: string;
  /** Optional call-to-action button */
  action?: EmptyStateAction;
  /** Size variant affecting spacing and typography */
  size?: EmptyStateSize;
}

/**
 * EmptyState component for displaying empty or no-results states.
 * Features an icon/illustration slot, title, description, and optional CTA.
 *
 * @example
 * ```tsx
 * <EmptyState
 *   icon={<MicrophoneIcon />}
 *   title="No songs yet"
 *   description="Add your first song to get started"
 *   action={{ label: "Add Song", onClick: () => {} }}
 * />
 * ```
 */
export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    {
      icon,
      title,
      description,
      action,
      size = 'md',
      className,
      ...props
    },
    ref
  ) => {
    const classNames = [styles.emptyState, styles[size], className]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={classNames} {...props}>
        {icon && (
          <div className={styles.iconWrapper} aria-hidden="true">
            {icon}
          </div>
        )}
        <h3 className={styles.title}>{title}</h3>
        {description && <p className={styles.description}>{description}</p>}
        {action && (
          <div className={styles.actionWrapper}>
            <Button
              variant={action.variant || 'primary'}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          </div>
        )}
      </div>
    );
  }
);

EmptyState.displayName = 'EmptyState';
